import { AppPermission } from './permissions.enum';

export const HeaderNames = {
  OpsClientUid: 'x-ops-client-uid',
  XsrfToken: 'xsrf-token',
  AuthTenantType: 'x-ops-auth-tenant-type',
  AuthTenantCode: 'x-ops-auth-tenant-code',
} as const;

export const GoAPI = {
  // Clients
  getClients: {
    url: 'clients',
    permissions: [],
  },
  createClients: {
    url: 'clients',
    permissions: [],
  },
  getClientByUid: {
    url: 'clients/{clientUid}',
    permissions: [AppPermission.CanViewModels],
  },
  updateClientByUid: {
    url: 'clients/{clientUid}',
    permissions: [],
  },

  // Languages
  getLanguages: {
    url: 'languages',
    permissions: [
      AppPermission.CanRegisterModel,
      AppPermission.CanVersioningModel,
    ],
  },
  createLanguages: {
    url: 'languages',
    permissions: [],
  },
  getLanguageByUid: {
    url: 'languages/{languageUid}',
    permissions: [],
  },
  updateLanguageByUid: {
    url: 'languages/{languageUid}',
    permissions: [],
  },

  // Model Status Summary
  getSummaryOfModelsForDashboard: {
    url: 'summary/models',
    permissions: [AppPermission.CanViewModels],
  },

  // Inferences
  getDashboardInferences: {
    url: 'inferences/models',
    permissions: [AppPermission.CanPerformModelMonitoring],
  },
  getInferencesSummary: {
    url: 'inferences/summary',
    permissions: [AppPermission.CanPerformModelMonitoring],
  },
  getModelKstat: {
    url: 'inferences/{modelUid}/kstat',
    permissions: [AppPermission.CanPerformModelMonitoring],
  },
  getRealtimeDataDriftSummary: {
    url: 'inferences/{modelUid}/drift-summary',
    permissions: [AppPermission.CanPerformModelMonitoring],
  },

  // Models
  getModels: {
    url: 'models',
    permissions: [
      AppPermission.CanViewModels,
      AppPermission.CanViewProductionRun,
    ],
  },
  getModelsSummary: {
    url: 'models/summary',
    permissions: [AppPermission.CanViewModels],
  },
  // Model Swagger
  getModelSwaggerByUid: {
    url: 'models/{modelUid}/model-serving-swagger',
    permissions: [AppPermission.CanDownloadSwagger],
  },
  // Model Static Scan Report
  getModelStaticScanReport: {
    url: 'models/{modelUid}/static-scan-report',
    permissions: [AppPermission.CanViewModelStaticScanReport],
  },
  getFeatureCharacteristics: {
    url: 'models/{modelUid}/feature-characteristics',
    permissions: [AppPermission.CanViewModels],
  },
  registerModel: {
    url: 'models',
    permissions: [
      AppPermission.CanRegisterModel,
      AppPermission.CanVersioningModel,
    ],
  },
  getModelByUid: {
    url: 'models/{modelUid}',
    permissions: [AppPermission.CanViewModels],
  },
  getModelByName: {
    url: 'models',
    Permissions: [AppPermission.CanViewModels],
  },

  updateModelByUid: {
    url: 'models/{modelUid}',
    permissions: [AppPermission.CanRegisterModel],
  },
  uploadRealtimeExeFiles: {
    url: 'models/{modelUid}/realtime-execution-files',
    permissions: [
      AppPermission.CanRunDevTesting,
      AppPermission.CanViewDevTesting,
      AppPermission.CanViewUATTesting,
    ],
  },
  uploadBatchExeFiles: {
    url: 'models/{modelUid}/batch-execution-files',
    permissions: [
      AppPermission.CanRunDevTesting,
      AppPermission.CanViewDevTesting,
      AppPermission.CanViewUATTesting,
    ],
  },
  getExeHistories: {
    url: 'models/{modelUid}/exec-histories',
    permissions: [
      AppPermission.CanRunDevTesting,
      AppPermission.CanViewDevTesting,
      AppPermission.CanViewUATTesting,
      AppPermission.CanViewProductionRun,
    ],
  },
  getExeHistoryDetailByUid: {
    url: 'models/{modelUid}/exec-histories/{executionUid}/detail',
    permissions: [
      AppPermission.CanRunDevTesting,
      AppPermission.CanViewDevTesting,
      AppPermission.CanViewUATTesting,
    ],
  },
  uploadModelArtifacts: {
    url: 'models/upload-model-artifacts',
    permissions: [
      AppPermission.CanRegisterModel,
      AppPermission.CanVersioningModel,
    ],
  },
  deployModel: {
    url: 'models/{modelUid}/deploy',
    permissions: [AppPermission.CanPromoteToProduction],
  },
  executeModel: {
    url: 'models/{modelUid}/execute',
    permissions: [
      AppPermission.CanRunDevTesting,
      AppPermission.CanViewDevTesting,
      AppPermission.CanViewUATTesting,
    ],
  },
  executeRealtimeModel: {
    url: 'models/{modelUid}/execute/realtime',
    permissions: [
      AppPermission.CanRunDevTesting,
      AppPermission.CanViewDevTesting,
      AppPermission.CanViewUATTesting,
    ],
  },
  executeBatchModel: {
    url: 'models/{modelUid}/execute/batch',
    permissions: [
      AppPermission.CanRunDevTesting,
      AppPermission.CanViewDevTesting,
      AppPermission.CanViewUATTesting,
    ],
  },
  getPrecalculatedDates: {
    url: 'models/precalculated-dates',
    permissions: [
      AppPermission.CanRunDevTesting,
      AppPermission.CanViewUATTesting,
    ],
  },
  getModelPromotionStatus: {
    url: 'models/{modelUid}/model-promotion-status',
    permissions: [
      AppPermission.CanPromoteToProduction,
      AppPermission.CanViewDeployments,
    ],
  },
  archiveModelVersion: {
    url: 'models/archive/{modelUid}',
    permissions: [AppPermission.CanArchiveModelVersion],
  },
  restoreModel: {
    url: 'models/restore/{modelUid}',
    permissions: [AppPermission.CanRestoreModel],
  },

  // Activity Log
  getModelEventLog: {
    url: 'events/{modelUid}',
    permissions: [AppPermission.CanViewModels],
  },
  downloadFailureLog: {
    url: 'download/logs/{modelUid}/{eventName}',
    permissions: [AppPermission.CanViewModels],
  },

  // Documents
  getDocumentInfo: {
    url: 'models/{modelUid}/documents/{fileUid}',
    permissions: [
      AppPermission.CanPromoteToProduction,
      AppPermission.CanViewDeployments,
    ],
  },
  downloadDocument: {
    url: 'models/{modelUid}/documents/{fileUid}/download',
    permissions: [
      AppPermission.CanPromoteToProduction,
      AppPermission.CanViewDeployments,
    ],
  },
  downloadDocuments: {
    url: 'models/{modelUid}/documents/download',
    permissions: [AppPermission.CanPromoteToProduction],
  },
  uploadDocumentsByUid: {
    url: 'models/{modelUid}/documents',
    permissions: [
      AppPermission.CanPromoteToProduction,
      AppPermission.CanViewDeployments,
    ],
  },
  getDocumentsList: {
    url: 'models/{modelUid}/documents',
    permissions: [AppPermission.CanViewPromotionHistory],
  },
  updateDocument: {
    url: 'models/{modelUid}/documents/{fileUid}',
    permissions: [
      AppPermission.CanPromoteToProduction,
      AppPermission.CanViewDeployments,
    ],
  },
  downloadBatchOutput: {
    url: 'models/{modelUid}/exec-histories/{executionUid}/download',
    permissions: [
      AppPermission.CanRunDevTesting,
      AppPermission.CanViewDevTesting,
      AppPermission.CanViewUATTesting,
    ],
  },
  isModelNameUnique: {
    url: 'models/is_model_name_unique',
    permissions: [AppPermission.CanRegisterModel],
  },
  populationExecutionAvailability: {
    url: 'models/{modelUid}/execution-availability',
    permissions: [
      AppPermission.CanRunDevTesting,
      AppPermission.CanViewUATTesting,
    ],
  },
  // Files
  batchExecutionFileMappings: {
    url: 'batch-execution-files/{fileUid}',
    permissions: [
      AppPermission.CanRunDevTesting,
      AppPermission.CanViewDevTesting,
      AppPermission.CanViewUATTesting,
    ],
  },
  createFileMapping: {
    url: 'batch-execution-files',
    permissions: [
      AppPermission.CanRunDevTesting,
      AppPermission.CanViewDevTesting,
      AppPermission.CanViewUATTesting,
    ],
  },
  getFilesForModel: {
    // url: 'batch-execution-files/model/{modelUid}', /// added extra model path to differentiate batchExecutionFileMappings and getFilesForModel
    url: `batch-execution-files`,
    permissions: [
      AppPermission.CanRunDevTesting,
      AppPermission.CanViewDevTesting,
      AppPermission.CanViewUATTesting,
    ],
  },
  getSelectedMapping: {
    url: `batch-execution-files/get/selected-mapping`,
    permissions: [
      AppPermission.CanRunDevTesting,
      AppPermission.CanViewDevTesting,
      AppPermission.CanViewUATTesting,
    ],
  },
  getExistingMappings: {
    url: `batch-execution-files/get/existing-mappings`,
    permissions: [
      AppPermission.CanRunDevTesting,
      AppPermission.CanViewDevTesting,
      AppPermission.CanViewUATTesting,
    ],
  },

  // Execution Histories
  getExecHistories: {
    url: `exec-histories`,
    permissions: [
      AppPermission.CanRunDevTesting,
      AppPermission.CanViewDevTesting,
      AppPermission.CanViewUATTesting,
    ],
  },
  getExecHistoryDetail: {
    url: `exec-histories/{executionUid}/detail`,
    permissions: [
      AppPermission.CanRunDevTesting,
      AppPermission.CanViewDevTesting,
      AppPermission.CanViewUATTesting,
    ],
  },
  downloadExecBatchOutput: {
    url: `exec-histories/{executionUid}/download`,
    permissions: [
      AppPermission.CanRunDevTesting,
      AppPermission.CanViewDevTesting,
      AppPermission.CanViewUATTesting,
    ],
  },

  // Translations
  getTranslations: {
    url: `translations/{lang}/{module}`,
    permissions: [],
  },

  opsAppFeatureConfig: {
    url: 'feature/config/{tenant}/{client}?',
    permissions: [],
  },

  // Comments
  getComments: {
    url: 'comments',
    permissions: [AppPermission.CanViewModels],
  },
  addComment: {
    url: 'comments',
    permissions: [
      AppPermission.CanPromoteToProduction,
      AppPermission.CanViewDeployments,
    ],
  },

  // Feedback
  sendFeedback: {
    url: 'feedback-form',
    permissions: [AppPermission.CanViewModels],
  },
  // Data Drift
  getDriftScoreDetails: {
    url: 'model-monitoring/data-drift/{executionType}/scores/detail',
    permissions: [AppPermission.CanAccessModelMonitoring],
  },
  getDriftFeatureDetails: {
    url: 'model-monitoring/data-drift/{executionType}/features/detail',
    permissions: [AppPermission.CanAccessModelMonitoring],
  },
  getDriftFeaturesList: {
    url: 'model-monitoring/data-drift/{executionType}/features/summary',
    permissions: [AppPermission.CanAccessModelMonitoring],
  },
  // Performance
  getPerformanceKeyMetricsSummary: {
    url: 'model-monitoring/performance/key-metrics/summary',
    permissions: [AppPermission.CanAccessModelMonitoring],
  },
  getPerformanceKeyMetricsDetail: {
    url: 'model-monitoring/performance/key-metrics/detail',
    permissions: [AppPermission.CanAccessModelMonitoring],
  },
  getPerformanceTimeSeries: {
    url: 'model-monitoring/performance/time-series',
    permissions: [AppPermission.CanAccessModelMonitoring],
  },
  getPerformanceApplicationStats: {
    url: 'model-monitoring/performance/quarterly-cohort',
    permissions: [AppPermission.CanAccessModelMonitoring],
  },

  // Monitoring
  getAllRealtimeSummaryStats: {
    url: 'monitoring/inference_summary_stats/realtime/{modelUid}/all',
    permissions: [AppPermission.CanViewDashboardStats],
  },
  getAllBatchInferenceSummaryStats: {
    url: 'monitoring/inference_summary_stats/batch/{modelUid}/all',
    permissions: [AppPermission.CanViewDashboardStats],
  },

  // Datasets
  getDatasets: {
    url: 'datasets',
    permissions: [],
  },
  getDatasetByUid: {
    url: 'datasets/{datasetUid}',
    permissions: [],
  },

  // Features
  getDatasetFeatures: {
    url: 'datasets/{datasetUid}/features',
    permissions: [
      AppPermission.CanRunDevTesting,
      AppPermission.CanViewDevTesting,
      AppPermission.CanViewUATTesting,
      AppPermission.CanDoProductionRun,
      AppPermission.CanViewProductionRun,
    ],
  },
  getDatasetFeaturesByUid: {
    url: 'datasets/{datasetUid}/features/{featureUid}',
    permissions: [],
  },

  // TODO: Routes Depreciate / Integrate

  getModelsInference: {
    url: 'models/inference',
    permissions: [AppPermission.CanPerformModelMonitoring],
  },
  featureAttr: {
    url: 'models/{modelUid}/feature-attributes',
    permissions: [],
  },
  testDataFiles: {
    url: 'models/{modelId}/batch/data-files',
    permissions: [],
  },
} as const;

// {clientName}/{intentType}/{product}
const remoteFileServicesCommonUrl = 'remote-files';
export const RemoteFilesServiceApi = {
  remoteFiles: {
    url: `${remoteFileServicesCommonUrl}/list/for/{intentType}`,
    permissions: [
      AppPermission.CanRunDevTesting,
      AppPermission.CanViewDevTesting,
      AppPermission.CanViewUATTesting,
    ],
  },
  remoteFilesForModel: {
    url: `${remoteFileServicesCommonUrl}/list/for/model/{modelUid}/intent/{intentType}`,
    permissions: [
      AppPermission.CanRunDevTesting,
      AppPermission.CanViewDevTesting,
      AppPermission.CanViewUATTesting,
    ],
  },
  remoteFileContents: {
    url: `${remoteFileServicesCommonUrl}/contents/for/{fileId}/{recordCount}/{random}`,
    permissions: [
      AppPermission.CanRunDevTesting,
      AppPermission.CanViewDevTesting,
      AppPermission.CanViewUATTesting,
    ],
  },
  remoteFileMetadata: {
    url: `${remoteFileServicesCommonUrl}/meta-data/for/{fileId}`,
    permissions: [
      AppPermission.CanRunDevTesting,
      AppPermission.CanViewDevTesting,
      AppPermission.CanViewUATTesting,
    ],
  },
  remoteFileGetLayout: {
    url: `${remoteFileServicesCommonUrl}/layouts/for/{fileId}`,
    permissions: [
      AppPermission.CanRunDevTesting,
      AppPermission.CanViewDevTesting,
      AppPermission.CanViewUATTesting,
    ],
  },
  remoteFileSaveLayout: {
    url: `${remoteFileServicesCommonUrl}/layout/save/for/{fileId}`,
    permissions: [
      AppPermission.CanRunDevTesting,
      AppPermission.CanViewDevTesting,
      AppPermission.CanViewUATTesting,
    ],
  },
  remoteFileUpload: {
    url: `${remoteFileServicesCommonUrl}/upload-file/for/{intentType}`,
    permissions: [
      AppPermission.CanRunDevTesting,
      AppPermission.CanViewDevTesting,
      AppPermission.CanViewUATTesting,
    ],
  },
};
export const GeneralAPI = {
  apiPrefix: { url: '/api', permissions: [] },
  authApiPrefix: { url: '/auth', permissions: [] },
  healthCheck: { url: '/health', permissions: [] },
  serverConfig: { url: '/server-config.json', permissions: [] },

  getUserProfile: { url: 'authz/user-profile', permissions: [] },

  getTeamMembers: {
    url: 'authz/users',
    permissions: [AppPermission.CanViewUsers],
  },
  getTeamMemberById: {
    url: 'authz/user/{id}',
    permissions: [AppPermission.CanViewUsers],
  },

  getRoles: {
    url: 'authz/roles',
    permissions: [
      AppPermission.CanViewPermissions,
      AppPermission.CanEditPermissions,
    ],
  },
  getPermissions: {
    url: 'authz/permissions',
    permissions: [
      AppPermission.CanViewPermissions,
      AppPermission.CanEditPermissions,
    ],
  },

  getRoleToPermissionMappings: {
    url: 'authz/map/permissions',
    permissions: [
      AppPermission.CanViewPermissions,
      AppPermission.CanEditPermissions,
    ],
  },
  updateRoleToPermissionMappings: {
    url: 'authz/map/permissions',
    permissions: [
      AppPermission.CanViewPermissions,
      AppPermission.CanEditPermissions,
    ],
  },

  getIdpRoleToAppRoleMappings: {
    url: 'authz/map/roles',
    permissions: [
      AppPermission.CanViewPermissions,
      AppPermission.CanEditPermissions,
    ],
  },
  updateIdpRoleToAppRoleMappings: {
    url: 'authz/map/roles',
    permissions: [
      AppPermission.CanViewPermissions,
      AppPermission.CanEditPermissions,
    ],
  },
  deleteIdpRoleToAppRoleMappingById: {
    url: 'authz/map/role/{id}',
    permissions: [
      AppPermission.CanViewPermissions,
      AppPermission.CanEditPermissions,
    ],
  },

  getPartyIdpMappings: {
    url: 'party-idp-mappings',
    permissions: [
      AppPermission.CanViewPermissions,
      AppPermission.CanEditPermissions,
    ],
  },
  addPartyIdpMappings: {
    url: 'party-idp-mappings/add',
    permissions: [
      AppPermission.CanViewPermissions,
      AppPermission.CanEditPermissions,
    ],
  },
  updatePartyIdpMappings: {
    url: 'party-idp-mappings/update',
    permissions: [
      AppPermission.CanViewPermissions,
      AppPermission.CanEditPermissions,
    ],
  },
  deletePartyIdpMappings: {
    url: 'party-idp-mappings/delete',
    permissions: [
      AppPermission.CanViewPermissions,
      AppPermission.CanEditPermissions,
    ],
  },
} as const;
